import { Component, OnInit } from '@angular/core';
import { ArticleType, FormuleDetailType, FormulesLexiClient, FormuleType, ListingArticlesLexiClient, MethodeDeCalculDeRemise, RechercheDxArticleDto } from '@lexi-clients/lexi';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { ArticleListService } from 'lexi-angular/src/app/services/article.service';
import { DxDataSourceService } from 'lexi-angular/src/app/shared/services/dx-data-source.service';
import { lastValueFrom } from 'rxjs';
import { CustomStoreService } from '../../services/custom-store.service';
import { PrixListService } from '../../services/prix.service';
import { AuthService } from "../../settings/auth.service";

@Component({
    selector: 'app-formule',
    templateUrl: './formule.component.html',
    styleUrls: ['./formule.component.scss'],
    standalone: false
})
export class FormuleComponent implements OnInit {
  formules: any = [];
  selectedFormule: any = null;

  expandedRowKeys: any[] = [];
  dataGridInstance: any; 

  methodeRemiseOptions: any[] = [];
  prixCustomStore: CustomStore;
  articleDataSource: DataSource;
  articleBundleDataSource: DataSource;
  listingArticleDataSource: any[] = [];
  
  isAddFormVisible: boolean = false;
  newFormuleDetail: any = {
    formuleDetailType: FormuleDetailType.article
  };
  editingFormuleDetail: any = {};
  currentParentFormule: any = null;

  selectedPrixId: number;
  currentSite: any = null;
  currentDeviseNbrDecimal: number = 0;

  canSaisirHeure: boolean = false;
  inclureArticlesBloques: boolean = false;
  // Types de détails de formule
  detailTypes = FormuleDetailType;
  detailTypeOptions: any[] = [
    { id: FormuleDetailType.article, name: "Article" },
    { id: FormuleDetailType.listingArticle, name: "Listing d'articles" }
  ];

  // Types de formules
  formuleTypes = FormuleType;
  formuleTypeOptions: any[] = [
    { id: FormuleType.bundle, name: "Bundle" },
    { id: FormuleType.kit, name: "Kit" }
  ];

  constructor(
    private readonly formuleService: FormulesLexiClient,
    private readonly prixListService: PrixListService,
    private readonly articleService: ArticleListService,
    private readonly dxDataSourceService: DxDataSourceService,
    private readonly authService: AuthService,
    private readonly listingArticleService: ListingArticlesLexiClient,
  ) {
    this.initMethodeRemiseOptions();
  }

  async ngOnInit() {

    this.prixCustomStore = new CustomStoreService(this.prixListService).getSelectBoxCustomStore();
    const prixList = await (this.prixCustomStore.load())
    this.selectedPrixId = prixList["data"][0].id;

    this.setArticleDataSource();

    // Initialisation de la source de données pour les listings d'articles
    try {
      const listingArticles = await lastValueFrom(this.listingArticleService.getAll());
      this.listingArticleDataSource = listingArticles as any[];
    } catch (error) {
      console.error('Erreur lors du chargement des listings d\'articles', error);
      this.listingArticleDataSource = [];
    }

    this.currentSite = this.authService.currentSite;
    this.currentDeviseNbrDecimal = this.currentSite?.devisePrincipale?.nombreDecimales || 0;

    await this.setDataSource();
  }

  initMethodeRemiseOptions(): void {
    // Différentes méthodes de remise
    this.methodeRemiseOptions = [
      { id: MethodeDeCalculDeRemise.aucune, name: "Aucune" },
      { id: MethodeDeCalculDeRemise.coefficient, name: "Coefficient" },
      // { id: MethodeDeCalculDeRemise.majoration, name: "Majoration" }, //Pas de remise Majoration
      { id: MethodeDeCalculDeRemise.minoration, name: "Minoration" },
      { id: MethodeDeCalculDeRemise.montant, name: "Montant" },
      { id: MethodeDeCalculDeRemise.pourcentage, name: "Pourcentage" },
    ];
  }

  getPriceFormat(): string {
    const decimals = this.currentDeviseNbrDecimal ?? 0;
    const formatString = `#,##0.${Array(decimals).fill('0').join('')}`;
    return formatString;
  }

  getTimeOptions() {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push({
          value: timeValue,
          display: timeValue
        });
      }
    }
    return times;
  }

  onPrixChanged()
  {
    this.setDataSource();
  }

  onArticleBundleValueChanged(e: any, cellInfo: any): void {
    // Ignorer l'appel initial déclenché à l'entrée en mode édition
    // cellInfo.value contient la valeur originale (immuable)
    if (e.value !== cellInfo.value) {
      cellInfo.setValue(e.value);
    }
  }

  setArticleDataSource()
  {
    const additionalParams = new Map([["inclureBloque", this.inclureArticlesBloques.toString()]]);
    this.articleDataSource = this.dxDataSourceService.getDataSourceForSelectBox(this.articleService, additionalParams);

    const bundleParams = new Map([
      ["inclureBloque", this.inclureArticlesBloques.toString()],
      ["articleType", ArticleType.bundle]
    ]);
    this.articleBundleDataSource = this.dxDataSourceService.getDataSourceForSelectBox(this.articleService, bundleParams);
  }

  async setDataSource(): Promise<void> {
    await this.getFormules();
  }

  async getFormules(): Promise<void> {
    try {
      const formules = await lastValueFrom(this.formuleService.getAll());
      this.formules = formules.map(formule => {
        if (formule.heureDebut) {
          formule.heureDebut = this.formatTimeForDisplay(formule.heureDebut);
        }
        if (formule.heureFin) {
          formule.heureFin = this.formatTimeForDisplay(formule.heureFin);
        }
        return formule;
      });

      await this.calculerPrixFormules();
    } catch (error) {
      console.error('Error lors de la récupération des formules', error);
    }
  }

  private formatTimeForDisplay(timeSpanValue: string): string {
    if (!timeSpanValue) return null;
    const parts = timeSpanValue.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeSpanValue;
  }

  async calculerPrixFormules(): Promise<void> {

    for (const formule of this.formules) {
      for (const detail of formule.formuleDetails) {
        try {
          const prix = this.getPrixFromTarif(detail.tarifs);
          detail.prix = prix;

          const montantRemise = this.calculateMontantRemise(detail);
          detail.montantRemise = montantRemise;

          const prixNet = prix + montantRemise;
          detail.prixNet = prixNet;
          
        } catch (error) {
          console.error('Erreur lors du calcul du prix de la formule:', error);
        }
      }
    }
  }

  getPrixFromTarif(tarifs: any[]): number {
    if (!tarifs || !Array.isArray(tarifs) || tarifs.length === 0) {
      console.warn('Aucun tarif disponible');
      return 0;
    }
    const tarif = tarifs.find(t => t.prixId === this.selectedPrixId);
    if (tarif) {
      return tarif.valeur || 0;
    }
    console.warn(`Aucun tarif trouvé pour le prix ID: ${this.selectedPrixId}`);
    return 0;
  }

  calculateMontantRemise(detail: any): number {
    if (!detail) {
      console.warn('Détail de formule non valide');
      return 0;
    }

    const prix = detail.prix || 0;
    const methode = detail.methodeRemise || MethodeDeCalculDeRemise.aucune;

    let montantRemise = 0;
    switch (methode) {
      case MethodeDeCalculDeRemise.aucune:
        break;
      case MethodeDeCalculDeRemise.pourcentage:
        montantRemise = - (detail.valueRemise || 0) * prix / 100;
        break;
      case MethodeDeCalculDeRemise.majoration:
        montantRemise = (detail.valueRemise || 0) ;
        break;
      case MethodeDeCalculDeRemise.minoration:
        montantRemise = - (detail.valueRemise || 0);
        break;
      case MethodeDeCalculDeRemise.montant:
        montantRemise = (detail.valueRemise || 0) - prix;
        break;
      case MethodeDeCalculDeRemise.coefficient:
        montantRemise = - (((1 - detail.valueRemise) || 0) * prix);
        break;
    }
    return montantRemise;
  }


  // ROW FORMULE CRUD
  async onRowInserting(e: any): Promise<void> 
  {    
    // Convertir les valeurs d'heures au format TimeSpan (HH:mm:ss)
    if (e.data.heureDebut) {
      e.data.heureDebut = this.convertToTimeSpan(e.data.heureDebut);
    }
    if (e.data.heureFin) {
      e.data.heureFin = this.convertToTimeSpan(e.data.heureFin);
    }
    await this.addFormule(e.data);
    this.setDataSource();
  }
  async onRowRemoving(id: number): Promise<void>
  {
    await this.deleteFormule(id);
    this.setDataSource();
  }
  async onRowUpdated(e: any): Promise<void> 
  {    
    // Convertir les valeurs d'heures au format TimeSpan (HH:mm:ss)
    if (e.data.heureDebut) {
      e.data.heureDebut = this.convertToTimeSpan(e.data.heureDebut);
    }
    
    if (e.data.heureFin) {
      e.data.heureFin = this.convertToTimeSpan(e.data.heureFin);
    }
    await this.updateFormule(e.data);
    this.setDataSource();
  }

  async onRowDetailRemoving(id: number): Promise<void>
  {
    // Sauvegarder les lignes développées avant modification
    const expandedKeys = this.dataGridInstance ? this.dataGridInstance.getVisibleRows()
      .filter((r: any) => r.rowType === 'data' && r.isExpanded)
      .map((r: any) => r.key) : [];

    await this.deleteFormuleDetail(id);
    await this.setDataSource();

    // Restaurer les lignes développées après le refresh
    setTimeout(() => {
      if (this.dataGridInstance && expandedKeys.length > 0) {
        expandedKeys.forEach((key: any) => this.dataGridInstance.expandRow(key));
      }
    }, 0);
  }  async onRowDetailUpdated(e: any): Promise<void>
  {
    // Sauvegarder les lignes développées avant modification
    const expandedKeys = this.dataGridInstance ? this.dataGridInstance.getVisibleRows()
      .filter((r: any) => r.rowType === 'data' && r.isExpanded)
      .map((r: any) => r.key) : [];

    await this.updateFormuleDetail(e.data);
    await this.setDataSource();

    // Restaurer les lignes développées après le refresh
    setTimeout(() => {
      if (this.dataGridInstance && expandedKeys.length > 0) {
        expandedKeys.forEach((key: any) => this.dataGridInstance.expandRow(key));
      }
    }, 0);
  }

    private convertToTimeSpan(timeValue: any): string {
    if (!timeValue) return null;
    
    if (typeof timeValue === 'string') {
      const parts = timeValue.split(':');
      
      if (parts.length === 2) {
        return `${parts[0]}:${parts[1]}:00`;
      }
      
      // on retourne la date au format HH:mm:ss
      return timeValue;
    }
  }

  onRowDetailInit(e: any, parentFormule: any): void {
    if (!parentFormule) {
      console.error('Impossible de récupérer la formule parente.');
      return;
    }    
    e.data.formuleId = parentFormule.id;
  }

  async addFormule(newFormule: any): Promise<void> {
    try {
      await lastValueFrom(this.formuleService.create(newFormule));
    } catch (error) {
      console.error('Erreur lors de l\'ajout de formule:', error);
    }
  }

  async updateFormule(updatedFormule: any): Promise<void> {
    try {
      await lastValueFrom(this.formuleService.update(updatedFormule));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la formule:', error);
    }
  }

  async deleteFormule(id: number): Promise<void> {
    try {
      await lastValueFrom(this.formuleService._delete(id));
    } catch (error) {
      console.error('Erreur lors de la suppression de la formule:', error);
    }
  }

  async updateFormuleDetail(updatedFormuleDetail: any): Promise<void> {
    try {
      await lastValueFrom(this.formuleService.updateDetail(updatedFormuleDetail));
    } catch (error) {
      console.error('Erreur lors de la mise à jour du détail de la formule:', error);
    }
  }
  
  async deleteFormuleDetail(id: number): Promise<void> {
    try {
      await lastValueFrom(this.formuleService.deleteDetail(id));
    } catch (error) {
      console.error('Erreur lors de la suppression du détail de la formule:', error);
    }
  }

  getElementTypeName(typeId: FormuleDetailType): string {
    const type = this.detailTypeOptions.find(t => t.id === typeId);
    return type ? type.name : 'Inconnu';
  }

  getFormuleTypeName(typeId: FormuleType): string {
    const type = this.formuleTypeOptions.find(t => t.id === typeId);
    return type ? type.name : 'Inconnu';
  }

  getMethodeRemiseName(methodeId: MethodeDeCalculDeRemise): string {
    const methode = this.methodeRemiseOptions.find(m => m.id === methodeId);
    return methode ? methode.name : 'Inconnu';
  }


  // Vérification des règles métier selon le type de formule
  isBundleFormule(formule: any): boolean {
    return formule && formule.formuleType === FormuleType.bundle;
  }

  isKitFormule(formule: any): boolean {
    return formule && formule.formuleType === FormuleType.kit;
  }

  // Obtenir les options de type de détail selon le type de formule
  getAvailableDetailTypes(formule: any): any[] {
    if (this.isKitFormule(formule)) {
      // Kit : seulement des articles, pas de listing
      return this.detailTypeOptions.filter(option => option.id === FormuleDetailType.article);
    }
    return this.detailTypeOptions; // Toutes les options pour Bundle et autres types
  }

  // Obtenir les options de méthode de remise selon le type de formule
  getAvailableMethodeRemiseOptions(formule: any): any[] {
    if (this.isKitFormule(formule)) {
      // Kit : seulement "Aucune"
      return this.methodeRemiseOptions.filter(option => option.id === MethodeDeCalculDeRemise.aucune);
    }
    return this.methodeRemiseOptions; // Toutes les options pour Bundle et autres types
  }

  // Appliquer les valeurs par défaut selon le type de formule
  applyFormuleTypeDefaults(formuleDetail: any, parentFormule: any): void {
    if (this.isKitFormule(parentFormule)) {
      // Forcer les valeurs pour Kit seulement - UTILISER LES VALEURS ENUM
      formuleDetail.formuleDetailType = FormuleDetailType.article;
      formuleDetail.methodeRemise = MethodeDeCalculDeRemise.aucune;
      formuleDetail.valueRemise = 0;
      formuleDetail.listingArticleId = null;
      // Pour les formules Kit, pas d'article de référence
      formuleDetail.isArticleReference = false;
    }
  }
  
  getPopupTitle(): string {
    if (this.newFormuleDetail.formuleDetailType === FormuleDetailType.listingArticle) {
      return 'Ajouter un listing d\'articles à la formule';
    }
    return 'Ajouter un article à la formule';
  }
  
  onDetailTypeChanged(e: any): void {
    if (e.value === FormuleDetailType.article) {
      this.newFormuleDetail.listingArticleId = null;
    } else if (e.value === FormuleDetailType.listingArticle) {
      this.newFormuleDetail.articleId = null;
    }
  }
  
  onDetailTypeChangedEdit = (e: any): void => {
    this.editingFormuleDetail = e.component.option('formData');
    if (e.value === FormuleDetailType.article) {
      this.editingFormuleDetail.listingArticleId = null;
    } else if (e.value === FormuleDetailType.listingArticle) {
      this.editingFormuleDetail.articleId = null;
    }
  }
  
  listingArticleDisplayExpr = (listingArticle: any): string => {
    return listingArticle && listingArticle.libelle ? listingArticle.libelle : '';
  }
    showAddForm(parentFormule: any): void {
    this.currentParentFormule = parentFormule;
    
    // Fermer d'abord le popup si ouvert
    this.isAddFormVisible = false;
    
    // Attendre un tick puis ouvrir
    setTimeout(() => {
      this.newFormuleDetail = {
        formuleId: parentFormule.id,
        quantite: 1,
        methodeRemise: MethodeDeCalculDeRemise.aucune,
        valueRemise: 0,
        formuleDetailType: FormuleDetailType.article,
        isArticleReference: false
      };
      
      // Appliquer les règles métier selon le type de formule
      this.applyFormuleTypeDefaults(this.newFormuleDetail, parentFormule);
      
      this.isAddFormVisible = true;
    }, 100);
  }
  
  async addFormuleDetailPopup(): Promise<void>  {
    // Sauvegarder les lignes développées avant modification
    const expandedKeys = this.dataGridInstance ? this.dataGridInstance.getVisibleRows()
      .filter((r: any) => r.rowType === 'data' && r.isExpanded)
      .map((r: any) => r.key) : [];
    if (this.newFormuleDetail.formuleDetailType === FormuleDetailType.article && !this.newFormuleDetail.articleId) {
      alert("Veuillez sélectionner un article.");
      return;
    }
    
    if (this.newFormuleDetail.formuleDetailType === FormuleDetailType.listingArticle && !this.newFormuleDetail.listingArticleId) {
      alert("Veuillez sélectionner un listing d'articles.");
      return;
    }

    // Validation des règles métier Kit seulement
    if (this.isKitFormule(this.currentParentFormule)) {
      if (this.newFormuleDetail.formuleDetailType !== FormuleDetailType.article) {
        alert("Les formules Kit ne peuvent contenir que des articles.");
        return;
      }
      if (this.newFormuleDetail.methodeRemise !== MethodeDeCalculDeRemise.aucune) {
        alert("Les formules Kit ne peuvent avoir que la méthode de remise 'Aucune'.");
        return;
      }
      if (this.newFormuleDetail.valueRemise !== 0) {
        alert("Les formules Kit ne peuvent avoir de valeur de remise.");
        return;
      }
    }

    // Appliquer les règles métier une dernière fois avant sauvegarde
    this.applyFormuleTypeDefaults(this.newFormuleDetail, this.currentParentFormule);
    
    this.newFormuleDetail.formuleId = this.currentParentFormule.id;

    try {
      await lastValueFrom(this.formuleService.createDetail(this.newFormuleDetail));
    } catch (error) {
      console.error('Erreur lors de l\'ajout du détail de la formule:', error);
    }

    this.isAddFormVisible = false;
    this.newFormuleDetail = { formuleDetailType: FormuleDetailType.article };
    this.currentParentFormule = null;

    await this.setDataSource();

    // Restaurer les lignes développées après le refresh
    setTimeout(() => {
      if (this.dataGridInstance && expandedKeys.length > 0) {
        expandedKeys.forEach((key: any) => this.dataGridInstance.expandRow(key));
      }
    }, 100);
  }

  cancelAddForm(): void {
    this.isAddFormVisible = false;
  }


  articleDisplayExpr = (article: RechercheDxArticleDto) => {
    return article && article.codeBo && article.libelleLong ? `${article.codeBo} - ${article.libelleLong}` : '';
  }
  
  onDataGridInitialized(e: any): void {
    this.dataGridInstance = e.component;
  }

  selectFormule(formule: any): void {
    this.selectedFormule = formule;
  }
}